import { useState, useEffect } from 'react'
import { categoriesApi } from '../lib/api'

export interface FilterState {
  categories: string[]
  listingTypes: string[]
  conditions: string[]
  priceRange: { min: number; max: number }
}

interface FilterSidebarProps {
  initialFilters?: FilterState
  onFiltersChange?: (filters: FilterState) => void
}

const defaultFilters: FilterState = {
  categories: [],
  listingTypes: [],
  conditions: [],
  priceRange: { min: 0, max: 0 },
}

const listingTypeOptions = [
  { id: 'RENT', label: 'Rent Only' },
  { id: 'SALE', label: 'Sale Only' },
  { id: 'RENT_OR_SALE', label: 'Rent or Sale' },
]

const conditionOptions = [
  { id: 'LIKE_NEW', label: 'Like New' },
  { id: 'GOOD', label: 'Good' },
  { id: 'FAIR', label: 'Fair' },
]

export default function FilterSidebar({
  initialFilters = defaultFilters,
  onFiltersChange,
}: FilterSidebarProps) {
  const [expandedSections, setExpandedSections] = useState<string[]>(['category', 'type', 'price', 'condition'])
  const [filters, setFilters] = useState<FilterState>({
    ...defaultFilters,
    ...initialFilters,
  })
  const [categoryOptions, setCategoryOptions] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    setFilters({ ...defaultFilters, ...initialFilters })
  }, [initialFilters])

  useEffect(() => {
    categoriesApi.getAll()
      .then((res) => setCategoryOptions(res.data.data.map((c: { name: string }) => ({ id: c.name, name: c.name }))))
      .catch(() => setCategoryOptions([]))
  }, [])

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    )
  }

  const updateFilters = (key: keyof FilterState, value: unknown) => {
    const newFilters = { ...filters, [key]: value }
    setFilters(newFilters)
    onFiltersChange?.(newFilters)
  }

  const handleClear = () => {
    setFilters(defaultFilters)
    onFiltersChange?.(defaultFilters)
  }

  const hasActiveFilters =
    filters.categories.length > 0 ||
    filters.listingTypes.length > 0 ||
    filters.conditions.length > 0 ||
    filters.priceRange.min > 0 ||
    filters.priceRange.max > 0

  return (
    <div className="bg-white rounded-xl border border-[var(--border)] p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg text-[var(--text-primary)]">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClear}
            className="text-xs text-[var(--forest-green)] hover:underline font-medium"
          >
            Clear
          </button>
        )}
      </div>

      {/* Categories */}
      <div className="border-b border-[var(--border)] pb-4 mb-4">
        <button
          onClick={() => toggleSection('category')}
          className="flex items-center justify-between w-full py-2 font-medium text-[var(--text-primary)]"
        >
          <span>Categories</span>
          <svg
            className={`w-5 h-5 text-[var(--text-muted)] transition-transform ${expandedSections.includes('category') ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {expandedSections.includes('category') && (
          <div className="space-y-2 mt-2">
            {categoryOptions.map((cat) => (
              <label key={cat.id} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={filters.categories.includes(cat.id)}
                  onChange={(e) => {
                    const newCats = e.target.checked
                      ? [...filters.categories, cat.id]
                      : filters.categories.filter((c) => c !== cat.id)
                    updateFilters('categories', newCats)
                  }}
                  className="w-4 h-4 rounded border-[var(--border)] text-[var(--forest-green)] focus:ring-[var(--forest-green)]"
                />
                <span className="text-[var(--text-secondary)] group-hover:text-[var(--forest-green)] transition-colors">
                  {cat.name}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Listing Type */}
      <div className="border-b border-[var(--border)] pb-4 mb-4">
        <button
          onClick={() => toggleSection('type')}
          className="flex items-center justify-between w-full py-2 font-medium text-[var(--text-primary)]"
        >
          <span>Listing Type</span>
          <svg
            className={`w-5 h-5 text-[var(--text-muted)] transition-transform ${expandedSections.includes('type') ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {expandedSections.includes('type') && (
          <div className="space-y-2 mt-2">
            {listingTypeOptions.map((type) => (
              <label key={type.id} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.listingTypes.includes(type.id)}
                  onChange={(e) => {
                    const newTypes = e.target.checked
                      ? [...filters.listingTypes, type.id]
                      : filters.listingTypes.filter((t) => t !== type.id)
                    updateFilters('listingTypes', newTypes)
                  }}
                  className="w-4 h-4 rounded border-[var(--border)] text-[var(--forest-green)] focus:ring-[var(--forest-green)]"
                />
                <span className="text-[var(--text-secondary)]">{type.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Price Range */}
      <div className="border-b border-[var(--border)] pb-4 mb-4">
        <button
          onClick={() => toggleSection('price')}
          className="flex items-center justify-between w-full py-2 font-medium text-[var(--text-primary)]"
        >
          <span>Price Range</span>
          <svg
            className={`w-5 h-5 text-[var(--text-muted)] transition-transform ${expandedSections.includes('price') ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {expandedSections.includes('price') && (
          <div className="space-y-2 mt-2">
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <label className="text-xs text-[var(--text-muted)] block mb-1">Min</label>
                <input
                  type="number"
                  min={0}
                  value={filters.priceRange.min}
                  onChange={(e) =>
                    updateFilters('priceRange', { ...filters.priceRange, min: Number(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)]"
                />
              </div>
              <span className="text-[var(--text-muted)] pt-5">–</span>
              <div className="flex-1">
                <label className="text-xs text-[var(--text-muted)] block mb-1">Max</label>
                <input
                  type="number"
                  min={0}
                  value={filters.priceRange.max}
                  onChange={(e) =>
                    updateFilters('priceRange', { ...filters.priceRange, max: Number(e.target.value) })
                  }
                  className="w-full px-3 py-2 border border-[var(--border)] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--forest-green)]"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Condition */}
      <div className="pb-4">
        <button
          onClick={() => toggleSection('condition')}
          className="flex items-center justify-between w-full py-2 font-medium text-[var(--text-primary)]"
        >
          <span>Condition</span>
          <svg
            className={`w-5 h-5 text-[var(--text-muted)] transition-transform ${expandedSections.includes('condition') ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {expandedSections.includes('condition') && (
          <div className="space-y-2 mt-2">
            {conditionOptions.map((condition) => (
              <label key={condition.id} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.conditions.includes(condition.id)}
                  onChange={(e) => {
                    const newConditions = e.target.checked
                      ? [...filters.conditions, condition.id]
                      : filters.conditions.filter((c) => c !== condition.id)
                    updateFilters('conditions', newConditions)
                  }}
                  className="w-4 h-4 rounded border-[var(--border)] text-[var(--forest-green)] focus:ring-[var(--forest-green)]"
                />
                <span className="text-[var(--text-secondary)]">{condition.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={handleClear}
        className="w-full py-2 text-[var(--forest-green)] text-sm font-medium hover:bg-[var(--bg-tint)] rounded-lg transition-colors"
      >
        Clear all filters
      </button>
    </div>
  )
}
